import { useEffect, useState, useMemo, useCallback } from 'react';
import { useTerritoryStore } from '../../store/territoryStore';
import ClusterStatsBar from './ClusterStatsBar';
import ClusterMap from './ClusterMap';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import type { SchoolStatus, OpportunityCluster } from '../../types';

// Module-level ZCTA cache — per-state, shared across mounts
const zctaCache = new Map<string, GeoJSON.FeatureCollection>();

interface ClusterDetailViewProps {
  cluster: OpportunityCluster;
  onBack: () => void;
}

export default function ClusterDetailView({ cluster, onBack }: ClusterDetailViewProps) {
  const {
    clusterDetail,
    isLoadingClusterDetail,
    clusterDetailError,
    fetchClusterDetail,
    clearClusterDetail,
  } = useTerritoryStore();

  const [zctaData, setZctaData] = useState<GeoJSON.FeatureCollection | null>(null);
  const [zctaLoading, setZctaLoading] = useState(true);

  const [activeFilters, setActiveFilters] = useState<Set<SchoolStatus>>(
    new Set<SchoolStatus>(['blast', 'competitor', 'open'])
  );
  const [showTerritories, setShowTerritories] = useState(true);
  const [showHighways, setShowHighways] = useState(false);
  const [showCities, setShowCities] = useState(true);

  const handleToggleFilter = useCallback((status: SchoolStatus) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  }, []);

  const handleShowAll = useCallback(() => {
    setActiveFilters(prev => {
      if (prev.size === 3) return new Set<SchoolStatus>();
      return new Set<SchoolStatus>(['blast', 'competitor', 'open']);
    });
  }, []);

  const filteredClusterDetail = useMemo(() => {
    if (!clusterDetail) return null;
    if (activeFilters.size === 3) return clusterDetail;
    return {
      ...clusterDetail,
      schools: clusterDetail.schools.filter(s => activeFilters.has(s.status)),
    };
  }, [clusterDetail, activeFilters]);

  useEffect(() => {
    fetchClusterDetail(cluster.centerLat, cluster.centerLng, cluster.radiusMiles);
    return () => clearClusterDetail();
  }, [cluster.centerLat, cluster.centerLng, cluster.radiusMiles, fetchClusterDetail, clearClusterDetail]);

  // Fetch ZCTA GeoJSON for all overlapping states (parallel, merged)
  useEffect(() => {
    if (!clusterDetail?.states?.length) return;

    let cancelled = false;
    setZctaLoading(true);

    const fetchStates = async () => {
      const results: GeoJSON.FeatureCollection[] = [];

      const uncached = clusterDetail.states.filter(s => !zctaCache.has(s));
      const cached = clusterDetail.states.filter(s => zctaCache.has(s)).map(s => zctaCache.get(s)!);
      results.push(...cached);

      if (uncached.length > 0) {
        const fetches = await Promise.allSettled(
          uncached.map(async (stateCode) => {
            const res = await fetch(`/data/zcta/${stateCode}.json`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data: GeoJSON.FeatureCollection = await res.json();
            zctaCache.set(stateCode, data);
            return data;
          })
        );
        for (const result of fetches) {
          if (result.status === 'fulfilled') results.push(result.value);
        }
      }

      if (cancelled) return;

      if (results.length > 0) {
        const merged: GeoJSON.FeatureCollection = {
          type: 'FeatureCollection',
          features: results.flatMap(fc => fc.features),
        };
        setZctaData(merged);
      }
      setZctaLoading(false);
    };

    fetchStates();
    return () => { cancelled = true; };
  }, [clusterDetail?.states]);

  if (isLoadingClusterDetail && !clusterDetail) {
    return (
      <div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-4 animate-pulse">
          <div className="flex items-center gap-4">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24" />
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-32" />
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm animate-pulse" style={{ height: '60vh' }}>
          <div className="h-full flex items-center justify-center text-gray-400 dark:text-gray-600">
            Loading cluster data...
          </div>
        </div>
      </div>
    );
  }

  if (clusterDetailError) {
    return (
      <div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-4">
          <button
            onClick={onBack}
            className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
          >
            &larr; Back to Analysis
          </button>
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
          <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <p className="text-red-700 dark:text-red-300 mb-3">{clusterDetailError}</p>
          <button
            onClick={() => fetchClusterDetail(cluster.centerLat, cluster.centerLng, cluster.radiusMiles)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/60 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!clusterDetail || !filteredClusterDetail) return null;

  return (
    <div>
      <ClusterStatsBar
        clusterLabel={cluster.label}
        opportunityScore={cluster.opportunityScore}
        stats={clusterDetail.stats}
        territoryCount={clusterDetail.territories.length}
        activeFilters={activeFilters}
        onToggleFilter={handleToggleFilter}
        onShowAll={handleShowAll}
        showTerritories={showTerritories}
        onToggleTerritories={() => setShowTerritories(prev => !prev)}
        showHighways={showHighways}
        onToggleHighways={() => setShowHighways(prev => !prev)}
        showCities={showCities}
        onToggleCities={() => setShowCities(prev => !prev)}
        onBack={onBack}
      />
      <ClusterMap
        cluster={{ centerLat: cluster.centerLat, centerLng: cluster.centerLng, radiusMiles: cluster.radiusMiles }}
        clusterDetail={filteredClusterDetail}
        zctaData={zctaData}
        showTerritories={showTerritories}
        showHighways={showHighways}
        showCities={showCities}
      />
      {zctaLoading && (
        <div className="text-center text-xs text-gray-400 dark:text-gray-500 mt-2">
          Loading territory boundaries...
        </div>
      )}
    </div>
  );
}
